import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { fmtMoney, fmtDelta } from "@/lib/money";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Icon } from "@/components/Icon";
import LiveBlackjackV2 from "@/components/live-blackjack/LiveBlackjackV2";
import LiveDealerVideo from "@/components/live-blackjack/LiveDealerVideo";
import { useLiveDealerStatus } from "@/hooks/useLiveDealerStatus";
import { NATIVE_GAME_IDS, ORIGINAL_PAGE_ROUTES } from "@/casino/nativeGames";
import ProviderCasinoShelf, { ProviderGameHost } from "@/components/casino/ProviderCasinoShelf";
import GameRulesModal from "@/components/casino/GameRulesModal";
import { GAME_SPECS } from "@/lib/gameSpecs";
import CasinoTile from "@/components/casino/CasinoTile";
import CasinoShelf from "@/components/casino/CasinoShelf";
import FeaturedComposition from "@/components/casino/FeaturedComposition";
import CategoryGrid from "@/components/casino/CategoryGrid";
import PlayWalletSelector from "@/components/ui/PlayWalletSelector";
import SegmentedPills from "@/components/ui/SegmentedPills";
import CategoryCards from "@/components/casino/CategoryCards";
import EmptyState from "@/components/ui/EmptyState";
import PlayingCard from "@/components/PlayingCard";

const IMG = {
  "dragon-tower": "/game-art/dragon-tower.jpeg",
  keno: "/game-art/keno.jpeg",
  plinko: "/game-art/plinko.jpeg",
  dice: "/game-art/dice.jpeg",
  coinflip: "/game-art/coinflip.jpeg",
  crash: "/game-art/crash.jpeg",
  mines: "/game-art/mines.jpeg",
  slots: "/game-art/slots.jpeg",
  hilo: "/game-art/hilo.jpeg",
  roulette: "/game-art/roulette.jpeg",
  blackjack: "/game-art/blackjack.jpeg",
  baccarat: "/game-art/baccarat.jpeg",
  live_dealer: "/game-art/live_dealer.jpeg",
};

export const ORIGINALS = [
  { id: "dragon-tower", label: "Dragon Tower", icon: "dragon-tower", img: IMG["dragon-tower"] },
  { id: "keno", label: "Keno", icon: "keno", img: IMG.keno },
  { id: "plinko", label: "Plinko", icon: "plinko", img: IMG.plinko },
  { id: "mines", label: "Mines", icon: "mines", img: IMG.mines, base: 2389 },
  { id: "dice", label: "Dice", icon: "dice", img: IMG.dice, base: 2161 },
  { id: "crash", label: "Crash", icon: "originals", img: IMG.crash, base: 1547 },
  { id: "blackjack", label: "Blackjack", icon: "blackjack", img: IMG.blackjack, base: 1343 },
  { id: "slots", label: "Slots", icon: "slots", img: IMG.slots, base: 1257 },
  { id: "roulette", label: "Roulette", icon: "roulette", img: IMG.roulette, base: 1173 },
  { id: "coinflip", label: "Coin Flip", icon: "coinflip", img: IMG.coinflip, base: 901 },
  { id: "hilo", label: "HiLo", icon: "hilo", img: IMG.hilo },
  { id: "baccarat", label: "Baccarat", icon: "baccarat", img: IMG.baccarat, base: 630 },
];
export const LIVE_GAMES = [
  { id: "liveblackjack", label: "Live Blackjack", tagline: "Real dealer · 7 seats · PCash", base: 214 },
  { id: "live", label: "Live Baccarat", tagline: "Real dealer · PCash", base: 168 },
];
export const GAME_LABEL = Object.fromEntries([...ORIGINALS, ...LIVE_GAMES].map((g) => [g.id, g.label]));

/* The native in-house game host map — the ONE place a native Original / live table is wired
   to its component. BOTH the desktop Casino page and the mobile game host (MobileCasinoGame)
   render from this, so they can never diverge. Adding a new native game = one entry here →
   it opens on desktop AND mobile automatically. Mines, Dice and Crash use their
   dedicated pages from ORIGINAL_PAGE_ROUTES on both platforms. Keep NATIVE_GAME_IDS in sync (a test enforces it). */
export function buildCasinoGames({ onResult, currency, edgeFor, liveTable }) {
  const edge = (id) => (edgeFor ? edgeFor(id) : null);
  return {
    live: <LiveBaccarat onResult={onResult} currency={currency} />,
    // Breakout reuses this same host via a preselected table (plan §5.3) — no new game id.
    liveblackjack: <LiveBlackjackV2 onResult={onResult} initialTable={liveTable || null} />,
  };
}

// Re-export the single-source id list (casino/nativeGames.js) and assert, in dev, that the
// host map hasn't drifted from it. A new native game added to buildCasinoGames OR the catalog
// without the other is flagged loudly on page load — on BOTH platforms, so it can never again
// be a silent mobile-only break (R-036).
export { NATIVE_GAME_IDS };
if (process.env.NODE_ENV !== "production") {
  const _hostIds = Object.keys(buildCasinoGames({ onResult: () => {}, currency: "coins", edgeFor: () => null }));
  const _missing = NATIVE_GAME_IDS.filter((id) => !_hostIds.includes(id));
  const _extra = _hostIds.filter((id) => !NATIVE_GAME_IDS.includes(id));
  if (_missing.length || _extra.length) {
    // eslint-disable-next-line no-console
    console.error(`[Casino] native game host drift — missing from host: [${_missing}] · extra in host: [${_extra}]. Keep buildCasinoGames (pages/Casino.jsx) in sync with NATIVE_GAME_IDS (casino/nativeGames.js).`);
  }
}

// Pretty provider label for provider-sourced tiles (keeps badges honest + data-driven).
const PROVIDER_LABEL = { jacktop: "JackTop", kerma: "Kerma", mock: "Sandbox" };
const providerLabel = (p) => PROVIDER_LABEL[p] || (p ? p.charAt(0).toUpperCase() + p.slice(1) : "Provider");

// Tagline shown on the rotating "Featured game" banner (one per Original).
const FEATURED_TAGLINE = {
  plinko: "Every bounce. A new possibility.",
  mines: "Cash out before you hit a mine",
  dice: "Roll your target, beat the house",
  crash: "Cash out before it busts",
  blackjack: "Hit 21, beat the dealer",
  slots: "Spin the reels",
  hilo: "Higher. Lower. Your move.",
  roulette: "Pick your number, spin to win",
  coinflip: "Heads or tails — double or nothing",
  baccarat: "Player or banker? Call it",
};

/* Live/Offline status pill — shows the animated red "Live" only when a dealer is
   actually broadcasting; otherwise a muted "Offline" so nothing overclaims. */
function LivePill({ live, label = "Live" }) {
  if (live) {
    return (
      <span className="flex w-fit items-center gap-1.5 rounded-full bg-[#EF4444] px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-white">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> {label}
      </span>
    );
  }
  return (
    <span className="flex w-fit items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-white/55 backdrop-blur">
      <span className="h-1.5 w-1.5 rounded-full bg-white/30" /> Offline
    </span>
  );
}

export default function Casino() {
  const navigate = useNavigate();
  const openOriginal = (id) => { if (!ORIGINAL_PAGE_ROUTES[id]) setLiveTable(null); return ORIGINAL_PAGE_ROUTES[id] ? navigate(ORIGINAL_PAGE_ROUTES[id]) : setActive(id); };
  const { user, refresh, streamerMode } = useAuth();
  const reduceMotion = useReducedMotion();
  const liveStatus = useLiveDealerStatus();
  const [active, setActive] = useState(null); // null = lobby
  // Featured game rotates — a random Original is spotlighted per page load.
  const featured = useMemo(() => ORIGINALS[Math.floor(Math.random() * ORIGINALS.length)], []);
  const [searchParams, setSearchParams] = useSearchParams();
  const liveOnly = searchParams.get("tab") === "live"; // "Live Dealer" nav → focus on live table games
  const [currency, setCurrency] = useState("coins");
  const [history, setHistory] = useState([]);
  const [edges, setEdges] = useState({});
  const [gatedGames, setGatedGames] = useState([]);
  const [catFilter, setCatFilter] = useState("lobby"); // lobby | originals | slots | live | table
  // "View all" opens a real collection grid for one category while preserving the lobby's
  // scroll position + active search/filters (restored on back). null = normal shelf lobby.
  const [viewAllCat, setViewAllCat] = useState(null); // "originals" | "live" | "table" | null
  const lobbyScrollRef = useRef(0);
  const openViewAll = (cat) => {
    lobbyScrollRef.current = typeof window !== "undefined" ? window.scrollY : 0;
    setViewAllCat(cat);
    if (typeof window !== "undefined") window.scrollTo({ top: 0 });
  };
  const closeViewAll = () => {
    setViewAllCat(null);
    // Restore the lobby scroll position after the shelves re-render.
    if (typeof window !== "undefined") requestAnimationFrame(() => window.scrollTo({ top: lobbyScrollRef.current }));
  };
  const [gameSearch, setGameSearch] = useState("");
  const [rulesOpen, setRulesOpen] = useState(false);
  const searchRef = useRef(null);
  // Make the casino search's keyboard hint truthful: Ctrl/⌘+K focuses the GAME search here (the
  // shortcut was previously sportsbook-only). Also respond to the shell header's context-aware
  // "open casino search" event so the global search bar targets games on this page.
  useEffect(() => {
    const focusSearch = () => { searchRef.current?.focus(); searchRef.current?.scrollIntoView({ block: "center", behavior: "smooth" }); };
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "k" || e.key === "K")) { e.preventDefault(); focusSearch(); }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("pnr-open-casino-search", focusSearch);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("pnr-open-casino-search", focusSearch); };
  }, []);
  // Provider live-dealer games (preview) shown IN the Live Dealers shelf next to ours.
  const [providerLive, setProviderLive] = useState([]);
  const [provLaunch, setProvLaunch] = useState(null);
  const [provLaunching, setProvLaunching] = useState(false); // dedupe-launch guard (UIB-11b)
  // Whether every visible shelf (native + provider) came up empty for the current search,
  // so we can show one unified "No games match" state instead of a blank page (UIB-10).
  const [providerEmpty, setProviderEmpty] = useState(false);
  // Breakout is a dedicated Live Dealers tile that opens the shared liveblackjack host with the
  // breakout table preselected. It only appears when the server catalog marks breakout visible
  // (intake-gated, so it stays hidden until the physical table is ready).
  const [liveTable, setLiveTable] = useState(null);
  const [breakoutVisible, setBreakoutVisible] = useState(false);
  const openBreakout = () => { setLiveTable("breakout"); setActive("liveblackjack"); };

  const loadHistory = () => api.get("/casino/history").then(({ data }) => setHistory(data)).catch(() => {});
  useEffect(() => { loadHistory(); }, []);
  useEffect(() => {
    api.get("/casino/bj/tables")
      .then(({ data }) => setBreakoutVisible((data || []).some((t) => t.table_id === "breakout" && t.visible)))
      .catch(() => setBreakoutVisible(false));
  }, []);
  useEffect(() => {
    api.get("/casino/featured")
      // Only genuine live-dealer provider games belong in Live Dealers. The backend already
      // splits on is_live_dealer; we re-assert it here so a stale provider "Live" label can
      // never slip a dealer-less RNG game (e.g. JackTop Teen Patti/Turbo Roulette) into it.
      .then(({ data }) => setProviderLive((data?.live || []).filter((g) => g.thumbnail_url && g.is_live_dealer)))
      .catch(() => setProviderLive([]));
  }, []);

  // Launch a provider (e.g. JackTop) game from its "provider:externalId" id.
  const launchProvider = async (game) => {
    // Ignore duplicate taps while a launch is already in flight (UIB-11b).
    if (provLaunching) return;
    const [provider, ext] = String(game.id || "").split(":");
    if (!provider || !ext) return;
    // Launch in the player's selected currency (PCoins GC or PCash SC). External providers
    // don't transact in the bonus wallet, so ask the player to switch first.
    if (currency === "bonus") {
      toast.error("Switch to PCoins or PCash to play provider games.");
      return;
    }
    const wallet = currency === "coins" ? "coins" : "cash";
    setProvLaunching(true);
    try {
      const { data } = await api.post(`/casino/providers/${provider}/launch`, {
        game_id: ext, wallet, locale: navigator.language || "en",
      });
      setProvLaunch({ ...data, wallet, gameName: game.name });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setProvLaunching(false);
    }
  };
  useEffect(() => { api.get("/casino/house-edges").then(({ data }) => { setEdges(data.edges || {}); setGatedGames(data.currency_gated || []); }).catch(() => {}); }, []);

  // Deep link support (UIA-12): /casino?game=<slug> auto-opens a native game (e.g. the
  // Home page links to /casino?game=crash). Read once on mount. If the slug isn't a known
  // native game, fall through to discovery (the lobby) rather than erroring — and clear the
  // param so a back-to-lobby tap isn't instantly re-hijacked by the stale query string.
  useEffect(() => {
    const slug = searchParams.get("game");
    if (!slug) return;
    if (ORIGINAL_PAGE_ROUTES[slug]) { navigate(ORIGINAL_PAGE_ROUTES[slug], { replace: true }); return; }
    if (GAME_LABEL[slug]) setActive(slug);
    setSearchParams((prev) => { const p = new URLSearchParams(prev); p.delete("game"); return p; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onResult = async () => { await refresh(); loadHistory(); };

  // Lobby shelf entrance stagger (BOLD_UI_DIRECTION §7) — one-shot, reduced-motion-guarded.
  const shelfStagger = {
    hidden: {},
    show: { transition: reduceMotion ? {} : { staggerChildren: 0.06, delayChildren: 0.04 } },
  };
  const shelfItem = reduceMotion
    ? { hidden: { opacity: 1 }, show: { opacity: 1 } }
    : {
        hidden: { opacity: 0, y: 16 },
        show: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } },
      };

  // Edge (%) that actually applies to the CURRENT currency: math games are fair (0) on coins/bonus.
  const edgeFor = (id) => {
    const e = edges[id];
    if (e == null) return null;
    if (gatedGames.includes(id)) return currency === "cash" ? e : 0;
    return e;
  };

  // SINGLE SOURCE OF TRUTH for the native game host — shared by desktop (here) and the
  // mobile host (mobile/screens/MobileCasinoGame.jsx). Add a new native Original/live table
  // in buildCasinoGames ONCE and it works on BOTH platforms. (A parity test asserts every
  // native-component game stays mobile-routable so this can never silently regress again.)
  const games = buildCasinoGames({ onResult, currency, edgeFor, liveTable });

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <GameRulesModal gameKey={rulesOpen ? active : null} onClose={() => setRulesOpen(false)} />
      <div className="px-4 py-8 sm:px-6 lg:px-8">
      {/* Page header + currency toggle — only in a game view; the lobby leads with the hero (CA-01). */}
      {active !== null && (
      <>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="mb-1 text-xs uppercase tracking-[0.2em] text-[#a1a1aa]">Casino</p>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-display text-4xl font-black tracking-tighter sm:text-5xl">{GAME_LABEL[active]}</h1>
            {GAME_SPECS[active] && (
              <button type="button" data-testid="game-rules-open" onClick={() => setRulesOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-full border border-surface-border bg-surface-5 px-3 py-1.5 text-xs font-bold text-[#a1a1aa] transition-colors hover:text-white">
                <Icon name="info" size={14} /> Game rules &amp; payouts
              </button>
            )}
          </div>
        </div>
        {/* Play-wallet selector — the explicit "selecting" control (§4.3), replacing the ad-hoc
            toggle. Drives the SAME currency state; bonus segment only when bonus > 0. */}
        <PlayWalletSelector
          value={currency}
          onChange={setCurrency}
          coins={user?.coins}
          cash={user?.cash}
          bonus={user?.bonus}
          mask={(s) => (streamerMode ? "•••" : s)}
          caption={false}
          className="w-full sm:w-auto"
        />
      </div>

      <p className="mb-6 flex items-center gap-1.5 text-sm text-[#a1a1aa]">Betting with
        {currency !== "bonus" && <Icon name={currency === "cash" ? "pnrcash" : "pnrcoin"} size={15} className={currency === "cash" ? "text-white" : "text-[#00D632]"} />}
        <b className={currency === "cash" ? "text-white" : "text-[#00D632]"}>{currency === "coins" ? "Free PCoins" : currency === "bonus" ? "Bonus Funds (non-withdrawable)" : "PCash"}</b>. Tap above to switch.</p>
      </>
      )}

      {active === null ? (
        liveOnly ? (
        /* ---------- LIVE DEALER PAGE — casino-page styling, live games from every provider ---------- */
        <div className="space-y-5" data-testid="casino-live-lobby">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00D632]">Casino</p>
              <h1 className="mt-1 font-display text-3xl font-black tracking-tight text-white">Live Dealers</h1>
            </div>
            <div className="sm:ml-1"><LivePill live={liveStatus.anyLive} /></div>
          </div>
          <p className="-mt-2 text-sm text-[#a1a1aa]">Real dealers streaming now — play in PCash across every studio.</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="live-dealers-section">
            {LIVE_GAMES.map((g) => (
              <CasinoTile key={g.id} name={g.label} img={IMG.live_dealer} badge="PRYMEBET Live Studio" provider live
                tag={(g.id === "liveblackjack" ? liveStatus.bjLive : liveStatus.baccaratLive) ? "LIVE" : null}
                onClick={() => openOriginal(g.id)} testid={`game-${g.id}`} />
            ))}
            {breakoutVisible && (
              <CasinoTile key="breakout" name="Breakout Blackjack"
                img="/game-art/bj-table-breakout.jpeg" badge="PRYMEBET Live Studio" provider live
                tag={liveStatus.tableLive?.breakout ? "LIVE" : null}
                onClick={openBreakout} testid="game-breakout" />
            )}
            {providerLive.map((g) => (
              <CasinoTile key={g.id} name={g.name} img={g.thumbnail_url} badge={`${providerLabel(g.provider)} Live`} provider live tag="LIVE"
                onClick={() => launchProvider(g)} testid={`provider-live-${g.id}`} />
            ))}
          </div>
          <ProviderGameHost launch={provLaunch} onClose={async () => {
            const closing = provLaunch;
            setProvLaunch(null);
            if (closing?.provider && closing?.session_id) {
              try { await api.post(`/casino/providers/${closing.provider}/sessions/${closing.session_id}/end`); } catch { /* UI still closes */ }
            }
          }} />
        </div>
        ) : (
        /* ---------- LOBBY (premium redesign — PREMIUM_UI_DIRECTION §5) ---------- */
        <div className="space-y-6 lg:space-y-8" data-testid="casino-lobby">
          {(() => {
            const q = gameSearch.trim().toLowerCase();
            const show = (cat) => catFilter === "lobby" || catFilter === cat;
            const matchOrig = q ? ORIGINALS.filter((g) => g.label.toLowerCase().includes(q)) : ORIGINALS;
            const matchLive = q ? LIVE_GAMES.filter((g) => g.label.toLowerCase().includes(q)) : LIVE_GAMES;
            const tableIds = new Set(["blackjack", "roulette", "baccarat", "hilo"]);
            const origTiles = (list) => list.map((g) => (
              <CasinoTile key={g.id} name={g.label} img={g.img} badge="PRYMEBET Original" tag={g.id === "mines" ? "HOT" : null} onClick={() => openOriginal(g.id)} testid={`game-${g.id}`} />
            ));
            const tableGames = ORIGINALS.filter((g) => tableIds.has(g.id) && (!q || g.label.toLowerCase().includes(q)));
            // Breakout is a dedicated live-dealer tile (gated on the server catalog); show it in
            // the lobby shelf + View-all grid alongside our other live tables, respecting search.
            const breakoutInLive = breakoutVisible && (!q || "breakout blackjack".includes(q));
            const liveTiles = [
              ...matchLive.map((g) => (
                <CasinoTile key={g.id} name={g.label} img={IMG.live_dealer} badge="PRYMEBET Live Studio" provider live
                  tag={(g.id === "liveblackjack" ? liveStatus.bjLive : liveStatus.baccaratLive) ? "LIVE" : null}
                  onClick={() => openOriginal(g.id)} testid={`game-${g.id}`} />
              )),
              ...(breakoutInLive ? [(
                <CasinoTile key="breakout" name="Breakout Blackjack"
                  img="/game-art/bj-table-breakout.jpeg" badge="PRYMEBET Live Studio" provider live
                  tag={liveStatus.tableLive?.breakout ? "LIVE" : null}
                  onClick={openBreakout} testid="game-breakout" />
              )] : []),
              ...providerLive.map((g) => (
                <CasinoTile key={g.id} name={g.name} img={g.thumbnail_url} badge={`${providerLabel(g.provider)} Live`} provider live tag="LIVE"
                  onClick={() => launchProvider(g)} testid={`provider-live-${g.id}`} />
              )),
            ];

            // ----- "View all" collection grid (a real grid, not a relabel) -----
            if (viewAllCat === "originals") {
              return (
                <CategoryGrid title="PRYMEBET Originals" subtitle="In-house games · server-settled rounds" icon="originals" count={ORIGINALS.length} onBack={closeViewAll} testid="grid-originals">
                  {origTiles(ORIGINALS)}
                </CategoryGrid>
              );
            }
            if (viewAllCat === "live") {
              return (
                <CategoryGrid title="Live Dealers" subtitle="Real dealers · PCash" icon="livedealer" count={matchLive.length + providerLive.length + (breakoutInLive ? 1 : 0)} onBack={closeViewAll} testid="grid-live">
                  {liveTiles}
                </CategoryGrid>
              );
            }
            if (viewAllCat === "table") {
              return (
                <CategoryGrid title="Table Games" subtitle="Blackjack · Roulette · Baccarat" icon="baccarat" count={ORIGINALS.filter((g) => tableIds.has(g.id)).length} onBack={closeViewAll} testid="grid-table">
                  {origTiles(ORIGINALS.filter((g) => tableIds.has(g.id)))}
                </CategoryGrid>
              );
            }

            // Whether the provider shelf is shown for this category (lobby/slots). It renders
            // DURING a search too (UIB-10) so provider games are searchable, not hidden on type.
            const showProvider = catFilter === "lobby" || catFilter === "slots";
            const nativeMatched =
              (show("originals") && matchOrig.length > 0) ||
              (show("live") && (matchLive.length > 0 || providerLive.length > 0 || breakoutInLive)) ||
              show("table");
            const tableMatched = show("table") && (!q || ORIGINALS.some((g) => tableIds.has(g.id) && g.label.toLowerCase().includes(q)));
            const nativeMatchedForSearch = q
              ? ((show("originals") && matchOrig.length > 0) || (show("live") && (matchLive.length > 0 || providerLive.length > 0 || breakoutInLive)) || tableMatched)
              : nativeMatched;

            const CAT_PILLS = [
              { id: "lobby", label: "Lobby", icon: "casino" },
              { id: "originals", label: "Originals", icon: "originals" },
              { id: "slots", label: "Slots", icon: "slots" },
              { id: "live", label: "Live Dealers", icon: "livedealer" },
              { id: "table", label: "Table Games", icon: "baccarat" },
            ];
            // Honest per-category counts for the illustrated cards (§3). Only categories with a
            // deterministic client-side count show one; lobby/slots are provider-driven → omit.
            const tableCount = ORIGINALS.filter((g) => tableIds.has(g.id)).length;
            const CAT_CARDS = CAT_PILLS.map((c) =>
              c.id === "originals" ? { ...c, count: ORIGINALS.length }
              : c.id === "live" ? { ...c, count: matchLive.length + providerLive.length }
              : c.id === "table" ? { ...c, count: tableCount }
              : c
            );

            return (
              <>
                {/* 1. Featured composition — ONE compact art-forward hero (§5), replacing the old
                    three ≥240px banners. Preserves the random-Original-per-load rotation. Desktop
                    adds a single compact promo beside it (≤200px), never the old 3-banner stack. */}
                <FeaturedComposition
                  featured={featured}
                  tagline={FEATURED_TAGLINE[featured.id]}
                  onPlay={() => openOriginal(featured.id)}
                  PromoLink={Link}
                  promo={{ to: "/rewards#daily", eyebrow: "Daily reward", title: "Free PCoins daily", subtitle: "Claim your drop every 24 hours", cta: "Claim now" }}
                />

                {/* 2. Search bar (full width, h-44) */}
                <div className="relative">
                  <Icon name="search" size={16} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[#71717a]" />
                  <input ref={searchRef} value={gameSearch} onChange={(e) => setGameSearch(e.target.value)} placeholder="Search games or providers" data-testid="casino-search-input"
                    className="h-11 w-full rounded-full border border-surface-border bg-surface-3 pl-10 pr-14 text-sm text-white placeholder:text-[#71717a] focus:border-pnr-green/50 focus-ring" />
                  <span className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded border border-surface-border px-1.5 py-0.5 text-[10px] font-bold text-[#71717a] lg:inline-block">Ctrl K</span>
                </div>

                {/* 3. Play-wallet selector — the active currency, the "selecting" control (§4.3). */}
                <PlayWalletSelector
                  value={currency}
                  onChange={setCurrency}
                  coins={user?.coins}
                  cash={user?.cash}
                  bonus={user?.bonus}
                  mask={(s) => (streamerMode ? "•••" : s)}
                  caption={false}
                  testidPrefix="lobby-cur"
                  className="w-full sm:max-w-md"
                />

                {/* 4. Illustrated category cards (BOLD_UI_DIRECTION §3) — big tappable cards that
                    replace the flat pill row in the LOBBY ONLY. Same value/onChange contract as
                    SegmentedPills (kept for other surfaces). Selected = green edge, not fill. */}
                <CategoryCards items={CAT_CARDS} value={catFilter} onChange={setCatFilter} testidPrefix="cat" ariaLabel="Game category" />

                {/* 5. Shelves — first Originals shelf's art + titles sit above the 844 fold.
                    Wrapped in a one-shot staggered motion container (§7), reduced-motion-guarded. */}
                <motion.div className="space-y-6 lg:space-y-8" variants={shelfStagger} initial="hidden" animate="show">
                  {/* Featured spotlight — curated marquee games, pinned near the top. Self-hides during
                      search and when there are no featured games (variant="featured"). */}
                  {showProvider && (
                    <motion.div variants={shelfItem}>
                      <ProviderCasinoShelf variant="featured" currency={currency} query={gameSearch} refresh={refresh} />
                    </motion.div>
                  )}
                  {show("originals") && matchOrig.length > 0 && (
                    <motion.div variants={shelfItem}>
                      <CasinoShelf icon="originals" title="PRYMEBET Originals" count={matchOrig.length} subtitle="In-house games · server-settled rounds" testid="shelf-originals" onViewAll={() => openViewAll("originals")}>
                        {origTiles(matchOrig)}
                      </CasinoShelf>
                    </motion.div>
                  )}

                  {show("live") && liveTiles.length > 0 && (
                    <motion.div variants={shelfItem}>
                      <CasinoShelf icon="livedealer" title="Live Dealers" count={matchLive.length + providerLive.length + (breakoutInLive ? 1 : 0)} subtitle="Real dealers · PCash" testid="shelf-live" tileWidth={280} onViewAll={() => openViewAll("live")}>
                        {liveTiles}
                      </CasinoShelf>
                    </motion.div>
                  )}
                  {show("table") && tableGames.length > 0 && (
                    <motion.div variants={shelfItem}>
                      <CasinoShelf icon="baccarat" title="Table Games" subtitle="Blackjack · Roulette · Baccarat" testid="shelf-table" onViewAll={() => openViewAll("table")}>
                        {origTiles(tableGames)}
                      </CasinoShelf>
                    </motion.div>
                  )}
                  {/* Provider shelf — rendered while searching too so provider games are searchable
                      (UIB-10). Reports back (onEmptyChange) whether it matched. */}
                  {showProvider && (
                    <motion.div variants={shelfItem}>
                      <ProviderCasinoShelf
                        currency={currency}
                        query={gameSearch}
                        catFilter={catFilter === "slots" ? "slots" : "lobby"}
                        refresh={refresh}
                        onEmptyChange={setProviderEmpty}
                      />
                    </motion.div>
                  )}
                  {/* Unified no-results state — only when NOTHING matched across native + provider
                      shelves for the active search (UIB-10). */}
                  {q && !nativeMatchedForSearch && (!showProvider || providerEmpty) && (
                    <EmptyState message={`No games match “${gameSearch.trim()}”.`} testid="casino-no-results" />
                  )}

                  {/* Compliance / rules note — a quiet footer at the very bottom of the lobby, not
                      wedged between game shelves. */}
                  <p className="border-t border-surface-border pt-5 text-center text-[11px] leading-relaxed text-[#52525b]" data-testid="casino-game-rules-note">
                    Game mechanics, settlement, voids, and interruption handling are described in the{" "}
                    <Link to="/game-rules" data-testid="casino-game-rules-link" className="text-[#71717a] underline underline-offset-2 hover:text-white">Sports &amp; Casino Game Rules</Link>. Play responsibly — 18+.
                  </p>
                </motion.div>
              </>
            );
          })()}
          <ProviderGameHost launch={provLaunch} onClose={async () => {
            const closing = provLaunch;
            setProvLaunch(null);
            if (closing?.provider && closing?.session_id) {
              try { await api.post(`/casino/providers/${closing.provider}/sessions/${closing.session_id}/end`); } catch { /* UI still closes */ }
            }
          }} />
        </div>
        )
      ) : (
        /* ---------- GAME VIEW ---------- */
        <>
          <button data-testid="casino-back" onClick={() => setActive(null)}
            className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#27272A] bg-[#141417] px-4 py-2 text-sm font-semibold text-[#a1a1aa] transition-colors hover:border-[#00D632] hover:text-white">
            <Icon name="chevron-left" size={16} /> All games
          </button>
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <AnimatePresence mode="wait">
                <motion.div key={active} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="rounded-2xl border border-[#27272A] bg-[#141417] p-6">
                  {games[active]}
                  {edges[active] != null && (
                    <div className="mt-5 border-t border-[#27272A] pt-3" data-testid={`game-edge-${active}`}>
                      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[#a1a1aa]">
                        <span className="rounded bg-[#1C1C21] px-2 py-0.5 font-bold uppercase tracking-wider text-[#71717a]">House edge</span>
                        {gatedGames.includes(active) ? (
                          <>
                            <span><b className="text-white">{edges[active]}%</b> on PCash</span>
                            <span className="text-[#52525b]">•</span>
                            <span><b className="text-[#00D632]">0%</b> on PCoins (fair play)</span>
                          </>
                        ) : (
                          <span><b className="text-white">{edges[active]}%</b> — applies to all play</span>
                        )}
                      </p>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
            <div>
              <h2 className="mb-3 font-display text-lg font-bold tracking-tight">Recent plays</h2>
              <div className="space-y-2">
                {history.length === 0 && <p className="text-sm text-[#a1a1aa]">No plays yet.</p>}
                {history.slice(0, 15).map((h) => (
                  <div key={h.id} data-testid={`recent-play-${h.id}`} className="flex items-center justify-between rounded-lg border border-[#27272A] bg-[#141417] px-3 py-2 text-sm">
                    <span className="capitalize text-[#a1a1aa]">{(h.game || "").replace(/_/g, " ")}</span>
                    <span className={`font-bold tabular-nums ${h.payout > h.bet ? "text-[#00D632]" : h.payout === h.bet ? "text-[#a1a1aa]" : "text-[#EF4444]"}`}>
                      {(() => {
                        const amt = h.payout > 0 ? h.payout - h.bet : -h.bet;
                        const sign = amt >= 0 ? "+" : "−";
                        const val = Math.abs(amt).toFixed(2);
                        return h.currency === "cash" ? `${sign}$${val}` : h.currency === "bonus" ? `${sign}$${val} bonus` : `${sign}${val} PCoins`;
                      })()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
      </div>
    </div>
  );
}

/* ---------- shared bet input ---------- */
function BetField({ bet, setBet, disabled, currency }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-[#a1a1aa]">Bet amount ({["cash", "bonus"].includes(currency) ? "$" : "coins"})</label>
      <div className="mt-1.5 flex gap-2">
        <Input type="number" min="0" step="0.01" value={bet} disabled={disabled} data-testid="bet-input"
          onChange={(e) => setBet(e.target.value)} className="w-40" />
        <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={() => setBet((parseFloat(bet) || 0) / 2 || "")} className="rounded-full">½</Button>
        <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={() => setBet((parseFloat(bet) || 0) * 2)} className="rounded-full">2×</Button>
      </div>
    </div>
  );
}

/* ---------- Baccarat Roadmaps ---------- */
// Build logical columns of P/B streaks (ties collapsed onto preceding hand)
function baccColumns(history) {
  const seq = [];
  history.forEach((w) => {
    if (w === "tie") { if (seq.length) seq[seq.length - 1].ties += 1; }
    else seq.push({ type: w, ties: 0 });
  });
  const cols = [];
  seq.forEach((e) => {
    const last = cols[cols.length - 1];
    if (last && last[0].type === e.type) last.push(e);
    else cols.push([e]);
  });
  return cols;
}

// Pack a flat list of same-kind runs into a 6-row grid with dragon-tail
function packRoad(items) {
  // items: array of values; group consecutive equal into columns
  const cols = [];
  items.forEach((v) => {
    const last = cols[cols.length - 1];
    if (last && last[0] === v) last.push(v);
    else cols.push([v]);
  });
  const cells = [];
  let baseCol = 0, maxCol = 0;
  cols.forEach((col) => {
    let colMax = baseCol;
    col.forEach((v, k) => {
      const r = k < 6 ? k : 5;
      const c = k < 6 ? baseCol : baseCol + (k - 5);
      cells.push({ r, c, val: v });
      if (c > colMax) colMax = c;
    });
    baseCol = colMax + 1;
    if (colMax > maxCol) maxCol = colMax;
  });
  return { cells, cols: cells.length ? maxCol + 1 : 0 };
}

// Derived road (Big Eye=1, Small=2, Cockroach=3): returns array of 'red'|'blue'
function derivedRoad(cols, offset) {
  const marks = [];
  for (let i = offset; i < cols.length; i++) {
    for (let j = 0; j < cols[i].length; j++) {
      if (j === 0) {
        const a = cols[i - offset - 1] ? cols[i - offset - 1].length : 0;
        const b = cols[i - 1] ? cols[i - 1].length : 0;
        marks.push(a === b ? "red" : "blue");
      } else {
        const prev = cols[i - offset] ? cols[i - offset].length : 0;
        marks.push(prev === j ? "blue" : "red");
      }
    }
  }
  return marks;
}

// Predict the derived-road color if the next result were `R` (player|banker)
function predictMark(cols, R, offset) {
  const c = cols.map((col) => col.slice());
  let i, j;
  const last = c[c.length - 1];
  if (last && last[0].type === R) { last.push({ type: R }); i = c.length - 1; j = last.length - 1; }
  else { c.push([{ type: R }]); i = c.length - 1; j = 0; }
  if (i < offset) return null;
  if (j === 0) {
    const a = c[i - offset - 1] ? c[i - offset - 1].length : 0;
    const b = c[i - 1] ? c[i - 1].length : 0;
    return a === b ? "red" : "blue";
  }
  const prev = c[i - offset] ? c[i - offset].length : 0;
  return prev === j ? "blue" : "red";
}

function RoadGrid({ title, cells, cols, shape }) {
  // shape: 'ring' (big road P/B), 'bead' (solid), 'derived' (red/blue)
  const bigColor = { player: "border-[#D4D4D8] text-[#D4D4D8]", banker: "border-[#EF4444] text-[#EF4444]" };
  const beadColor = { player: "bg-[#D4D4D8]", banker: "bg-[#EF4444]", tie: "bg-[#00D632]" };
  const derColor = { red: "border-[#EF4444]", blue: "border-[#D4D4D8]" };
  const L = { player: "P", banker: "B", tie: "T" };
  return (
    <div className="rounded-lg border border-[#27272A] bg-[#141417] p-2">
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#a1a1aa]">{title}</p>
      {cells.length === 0 ? (
        <p className="py-2 text-center text-[10px] text-[#a1a1aa]">—</p>
      ) : (
        <div className="overflow-x-auto">
          <div className="grid gap-0.5 bg-[#1C1C21] p-1"
            style={{ gridTemplateRows: "repeat(6, 1.1rem)", gridAutoColumns: "1.1rem", gridAutoFlow: "column", width: `${Math.max(cols, 1) * 1.2 + 0.5}rem` }}>
            {cells.map((cell, i) => {
              const style = { gridColumnStart: cell.c + 1, gridRowStart: cell.r + 1 };
              if (shape === "bead")
                return <div key={i} style={style} className={`flex h-[1.1rem] w-[1.1rem] items-center justify-center rounded-full text-[8px] font-black text-white ${beadColor[cell.val.type]}`}>{L[cell.val.type]}</div>;
              if (shape === "ring")
                return <div key={i} style={style} className={`relative flex h-[1.1rem] w-[1.1rem] items-center justify-center rounded-full border-2 bg-[#141417] text-[8px] font-black ${bigColor[cell.val.type]}`}>{L[cell.val.type]}{cell.val.ties > 0 && <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full bg-[#00D632]" />}</div>;
              // derived
              return <div key={i} style={style} className={`h-[0.9rem] w-[0.9rem] rounded-full border-2 ${derColor[cell.val]} ${cell.val === "red" ? "bg-[#EF4444]/10" : "bg-[#D4D4D8]/10"}`} />;
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function PredDot({ color }) {
  if (!color) return <span className="inline-block h-3 w-3 rounded-full border border-dashed border-[#d4d4d8]" />;
  return <span className={`inline-block h-3 w-3 rounded-full border-2 ${color === "red" ? "border-[#EF4444] bg-[#EF4444]/20" : "border-[#D4D4D8] bg-[#D4D4D8]/20"}`} />;
}

function BaccaratRoads({ history }) {
  const counts = history.reduce((a, w) => { a[w] = (a[w] || 0) + 1; return a; }, {});
  const cols = baccColumns(history);
  // Bead plate: raw sequence (incl. ties) packed in columns of 6, no streak grouping
  const bead = (() => {
    const cells = []; let maxCol = 0;
    history.forEach((w, i) => { const c = Math.floor(i / 6), r = i % 6; cells.push({ r, c, val: { type: w } }); if (c > maxCol) maxCol = c; });
    return { cells, cols: history.length ? maxCol + 1 : 0 };
  })();
  const big = (() => {
    const cells = []; let baseCol = 0, maxCol = 0;
    cols.forEach((col) => {
      let colMax = baseCol;
      col.forEach((e, k) => {
        const r = k < 6 ? k : 5, c = k < 6 ? baseCol : baseCol + (k - 5);
        cells.push({ r, c, val: e }); if (c > colMax) colMax = c;
      });
      baseCol = colMax + 1; if (colMax > maxCol) maxCol = colMax;
    });
    return { cells, cols: cells.length ? maxCol + 1 : 0 };
  })();
  const bigEye = packRoad(derivedRoad(cols, 1));
  const small = packRoad(derivedRoad(cols, 2));
  const roach = packRoad(derivedRoad(cols, 3));

  const preds = [
    { side: "player", label: "If Player", cls: "text-[#D4D4D8]" },
    { side: "banker", label: "If Banker", cls: "text-[#EF4444]" },
  ].map((p) => ({
    ...p,
    eye: predictMark(cols, p.side, 1),
    sml: predictMark(cols, p.side, 2),
    rch: predictMark(cols, p.side, 3),
  }));

  return (
    <div className="space-y-2 rounded-xl border border-[#27272A] bg-[#141417] p-3" data-testid="baccarat-roads">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#a1a1aa]">Roadmaps</p>
        <div className="flex gap-3 text-xs font-semibold">
          <span className="text-[#D4D4D8]">P {counts.player || 0}</span>
          <span className="text-[#EF4444]">B {counts.banker || 0}</span>
          <span className="text-[#00D632]">T {counts.tie || 0}</span>
        </div>
      </div>

      <RoadGrid title="Bead Plate" cells={bead.cells} cols={bead.cols} shape="bead" />
      <RoadGrid title="Big Road" cells={big.cells} cols={big.cols} shape="ring" />
      <div className="grid grid-cols-3 gap-2">
        <RoadGrid title="Big Eye Boy" cells={bigEye.cells} cols={bigEye.cols} shape="derived" />
        <RoadGrid title="Small Road" cells={small.cells} cols={small.cols} shape="derived" />
        <RoadGrid title="Cockroach" cells={roach.cells} cols={roach.cols} shape="derived" />
      </div>

      {/* Trend predictions (ask road) */}
      <div className="rounded-lg border border-[#27272A] bg-[#141417] p-2" data-testid="baccarat-predictions">
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#a1a1aa]">Trend prediction — what the next hand does to the roads</p>
        <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
          {preds.map((p) => (
            <div key={p.side} data-testid={`pred-${p.side}`} className="flex items-center justify-between rounded-md bg-[#141417] px-2.5 py-1.5">
              <span className={`text-xs font-bold ${p.cls}`}>{p.label} wins</span>
              <span className="flex items-center gap-3 text-[10px] text-[#a1a1aa]">
                <span className="flex items-center gap-1">Eye <PredDot color={p.eye} /></span>
                <span className="flex items-center gap-1">Sm <PredDot color={p.sml} /></span>
                <span className="flex items-center gap-1">Cr <PredDot color={p.rch} /></span>
              </span>
            </div>
          ))}
        </div>
        <p className="mt-1.5 text-[9px] text-[#a1a1aa]">Red = trend continues / follows the pattern · Blue = trend breaks. Dashed = not enough hands yet.</p>
      </div>
    </div>
  );
}

/* ---------- Baccarat ---------- */
// Baccarat cards carry the suit as a glyph (♠♥♦♣); map it to the shared component's
// suit code (S/H/D/C) so it renders through the same premium <PlayingCard>. Display only.
const BACC_SUIT_CODE = { "♠": "S", "♥": "H", "♦": "D", "♣": "C" };
function BaccCard({ card }) {
  return <PlayingCard card={{ rank: card.rank, suit: BACC_SUIT_CODE[card.suit] || card.suit }} size="lg" />;
}
/* ---------- Live Dealer Baccarat (admin-controlled) ---------- */
export function LiveBaccarat({ onResult, currency }) {
  const { user } = useAuth();
  const [round, setRound] = useState(null);
  const [channel, setChannel] = useState("");
  const [baccStream, setBaccStream] = useState(null);
  const [bet, setBet] = useState(10);
  const [side, setSide] = useState("player");
  const [busy, setBusy] = useState(false);
  const [roads, setRoads] = useState([]);
  const [liveCur, setLiveCur] = useState("cash");
  const lastSettled = useRef(null);
  const parent = typeof window !== "undefined" ? window.location.hostname : "localhost";
  const hasBonus = (user?.bonus || 0) > 0;

  useEffect(() => { api.get("/settings").then(({ data }) => { setChannel(data.twitch_channel || "pnrbet"); setBaccStream(data.baccarat_stream || null); }).catch(() => setChannel("pnrbet")); }, []);
  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/live/current");
        if (!active) return;
        setRound(data);
        if (data.status === "settled" && data.round_number !== lastSettled.current) {
          lastSettled.current = data.round_number;
          setRoads((p) => [...p, data.winner].slice(-80));
          onResult();
        }
      } catch (e) { /* ignore */ }
    };
    poll();
    const id = setInterval(poll, 3000);
    return () => { active = false; clearInterval(id); };
  }, [onResult]);

  const placeBet = async () => {
    setBusy(true);
    try {
      await api.post("/casino/live/bet", { side, bet: parseFloat(bet), currency: liveCur });
      toast.success(`Bet placed on ${side.replace("_", " ").toUpperCase()}`);
      onResult();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const SIDES = [
    { id: "player", label: "Player", sub: "1:1" },
    { id: "banker", label: "Banker", sub: "0.95:1" },
    { id: "tie", label: "Tie", sub: "8:1" },
  ];
  const BONUS_SIDES = [
    { id: "player_bonus", label: "Player Bonus", sub: "up to 30:1" },
    { id: "banker_bonus", label: "Banker Bonus", sub: "up to 30:1" },
  ];
  const status = round?.status || "none";
  const canBet = status === "betting";
  const statusText = { betting: "Betting OPEN", locked: "Betting closed — dealing", settled: "Round settled", none: "Waiting for dealer" }[status];
  const statusColor = { betting: "text-[#00D632]", locked: "text-[#00D632]", settled: "text-[#a1a1aa]", none: "text-[#a1a1aa]" }[status];

  return (
    <div className="space-y-5">
      <h2 className="font-display text-2xl font-black tracking-tight">Live Baccarat <span className="text-sm font-medium text-[#a1a1aa]">Live Dealer · PCash{hasBonus ? " or bonus" : ""}</span></h2>

      {hasBonus && (
        <div className="flex items-center gap-2" data-testid="live-currency-toggle">
          <span className="text-xs uppercase tracking-wide text-[#a1a1aa]">Play with:</span>
          <button data-testid="live-cur-cash" onClick={() => setLiveCur("cash")}
            className={`rounded-full border px-3 py-1 text-xs font-bold ${liveCur === "cash" ? "border-[#00D632] bg-[#00D632] text-[#0A0A0A]" : "border-[#27272A] bg-[#141417] text-[#a1a1aa]"}`}>PCash ${(user?.cash || 0).toFixed(2)}</button>
          <button data-testid="live-cur-bonus" onClick={() => setLiveCur("bonus")}
            className={`rounded-full border px-3 py-1 text-xs font-bold ${liveCur === "bonus" ? "border-surface-border bg-white/5 text-[#00D632]" : "border-[#27272A] bg-[#141417] text-[#a1a1aa]"}`}>Bonus ${(user?.bonus || 0).toFixed(2)}</button>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-[#27272A] bg-black" data-testid="baccarat-twitch">
        {baccStream?.stream_url ? (
          <LiveDealerVideo config={{ stream_url: baccStream.stream_url, video_provider: baccStream.video_provider, fallback_stream_url: baccStream.fallback_stream_url }} />
        ) : channel ? (
          <iframe title="live-stream" src={`https://player.twitch.tv/?channel=${channel}&parent=${parent}&muted=true`} height="320" width="100%" allowFullScreen frameBorder="0" style={{ display: "block" }} />
        ) : <div className="flex h-40 items-center justify-center text-sm text-white/60">Loading stream…</div>}
      </div>

      <div className="flex items-center justify-between rounded-xl bg-[#1C1C21] px-4 py-3" data-testid="live-status">
        <span className="text-sm">Round <b>#{round?.round_number ?? "—"}</b></span>
        <span className={`text-sm font-bold ${statusColor}`}>{statusText}</span>
      </div>

      {status === "settled" && round?.winner && (
        <div className="rounded-xl bg-[#0A0A0A] p-4 text-center" data-testid="live-winner">
          <span className="font-display text-xl font-black text-[#00D632]">{round.winner.toUpperCase()} WINS</span>
        </div>
      )}

      <div className="grid grid-cols-3 gap-2">
        {SIDES.map((s) => (
          <button key={s.id} data-testid={`live-side-${s.id}`} onClick={() => setSide(s.id)} disabled={!canBet}
            className={`rounded-xl border-2 py-3 text-center transition-colors disabled:opacity-50 ${side === s.id ? "border-[#00D632] bg-[#00D632]/10" : "border-[#27272A] bg-[#141417] hover:border-[#0A0A0A]/20"}`}>
            <p className="font-display font-bold">{s.label} <span className="text-[10px] text-[#a1a1aa]">{s.sub}</span></p>
            <p className="text-[10px] text-[#a1a1aa]">pool {round?.pool?.[s.id]?.toFixed(0) ?? 0}</p>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2">
        {BONUS_SIDES.map((s) => (
          <button key={s.id} data-testid={`live-side-${s.id}`} onClick={() => setSide(s.id)} disabled={!canBet}
            className={`rounded-xl border-2 py-3 text-center transition-colors disabled:opacity-50 ${side === s.id ? "border-surface-border bg-white/5" : "border-[#27272A] bg-[#141417] hover:border-surface-border"}`}>
            <p className="font-display font-bold text-[#00D632]">{s.label} <span className="text-[10px] text-[#a1a1aa]">{s.sub}</span></p>
            <p className="text-[10px] text-[#a1a1aa]">pool {round?.pool?.[s.id]?.toFixed(0) ?? 0}</p>
          </button>
        ))}
      </div>
      <p className="text-center text-[10px] text-[#a1a1aa]">Bonus pays when your side wins: natural 1:1 · by 4 = 1:1 · 5 = 2:1 · 6 = 4:1 · 7 = 6:1 · 8 = 10:1 · 9 = 30:1</p>

      <BetField bet={bet} setBet={setBet} disabled={!canBet || busy} currency={liveCur} />
      <Button onClick={placeBet} disabled={!canBet || busy} data-testid="live-place-bet" className="w-full rounded-full bg-[#00D632] py-6 font-bold text-[#0A0A0A] hover:bg-[#00B829] disabled:opacity-50">
        {canBet ? (busy ? "Placing..." : "Place bet") : "Betting closed"}
      </Button>

      <BaccaratRoads history={roads} />
    </div>
  );
}
